import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  FileText, User, Calendar, CheckCircle, XCircle, Send,
  Pencil, Trash2, ChevronRight, Clock,
} from 'lucide-react'
import { offerten as offertenApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Select } from '../../components/ui/Select'
import { Input } from '../../components/ui/Input'
import { Textarea } from '../../components/ui/Textarea'
import { offertStatusBadge } from '../../components/ui/Badge'
import { LoadingSpinner, ErrorMessage } from '../../components/ui/EmptyState'
import { formatDatum, formatWaehrung, berechneTotalOfferte } from '../../lib/utils'
import type { Offerte } from '../../types'

const STATUS_FLOW = [
  { from: 'entwurf',   action: 'Senden',        to: 'gesendet',    icon: <Send size={14} />, color: 'bg-blue-600' },
  { from: 'gesendet',  action: 'Als angenommen', to: 'angenommen',  icon: <CheckCircle size={14} />, color: 'bg-green-600' },
  { from: 'gesendet',  action: 'Als abgelehnt',  to: 'abgelehnt',   icon: <XCircle size={14} />, color: 'bg-red-600' },
]

export function OffertDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editForm, setEditForm] = useState<{ status: string; gueltig_bis: string; notizen: string } | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['offerte', id],
    queryFn: () => offertenApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (d: Partial<Offerte>) => offertenApi.update(id!, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offerte', id] })
      qc.invalidateQueries({ queryKey: ['offerten'] })
      setEditOpen(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => offertenApi.delete(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offerten'] })
      navigate('/offerten')
    },
  })

  if (isLoading) return <><TopBar title="Offerte" backTo="/offerten" /><LoadingSpinner /></>
  if (error || !data) return <><TopBar title="Fehler" backTo="/offerten" /><ErrorMessage message="Offerte nicht gefunden" /></>

  const positionen = data.positionen ?? []
  const total = berechneTotalOfferte(positionen)
  const nextActions = STATUS_FLOW.filter((s) => s.from === data.status)

  function openEdit() {
    if (!data) return
    setEditForm({ status: data.status, gueltig_bis: data.gueltig_bis ?? '', notizen: data.notizen ?? '' })
    setEditOpen(true)
  }

  return (
    <>
      <TopBar
        title={data.nummer}
        subtitle={data.kunde_name}
        backTo="/offerten"
        rightAction={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" icon={<Pencil size={14} />} onClick={openEdit}>
              Bearbeiten
            </Button>
          </div>
        }
      />

      <div className="px-4 py-3 space-y-4 pb-8">

        {/* Status + Aktionen */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            {offertStatusBadge(data.status)}
            {data.gueltig_bis && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Clock size={12} />
                Gültig bis {formatDatum(data.gueltig_bis)}
              </div>
            )}
          </div>

          {nextActions.length > 0 && (
            <div className="flex flex-col gap-2">
              {nextActions.map((action) => (
                <Button
                  key={action.to}
                  fullWidth
                  size="sm"
                  icon={action.icon}
                  loading={updateMutation.isPending}
                  onClick={() => updateMutation.mutate({ ...data, status: action.to as Offerte['status'] })}
                  className={action.color}
                >
                  {action.action} markieren
                  <ChevronRight size={14} className="ml-auto" />
                </Button>
              ))}
            </div>
          )}
        </Card>

        {/* Info */}
        <Card>
          <CardHeader><CardTitle><User size={15} className="inline mr-1.5" />Details</CardTitle></CardHeader>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Kunde</p>
              <p className="font-medium text-gray-900">{data.kunde_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Ersteller</p>
              <p className="font-medium text-gray-900">{data.ersteller}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Erstellt am</p>
              <p className="font-medium text-gray-900">{formatDatum(data.erstellt_am)}</p>
            </div>
            {data.gueltig_bis && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Gültig bis</p>
                <p className="font-medium text-gray-900">{formatDatum(data.gueltig_bis)}</p>
              </div>
            )}
            {data.notizen && (
              <div className="col-span-2">
                <p className="text-xs text-gray-500 mb-0.5">Notizen</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.notizen}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Positionen */}
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <CardTitle><FileText size={15} className="inline mr-1.5" />Positionen ({positionen.length})</CardTitle>
          </div>
          {positionen.length === 0 ? (
            <p className="text-sm text-gray-400 italic text-center py-6">Keine Positionen</p>
          ) : (
            <div>
              {positionen.map((pos, i) => {
                const netto = pos.menge * pos.einzelpreis
                const rabatt = pos.rabatt > 0 ? netto * (pos.rabatt / 100) : 0
                const total_pos = netto - rabatt
                return (
                  <div key={pos.id ?? i} className="px-4 py-3 border-b border-gray-50 last:border-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{pos.beschreibung}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {pos.menge} {pos.einheit} × {formatWaehrung(pos.einzelpreis)}
                          {pos.rabatt > 0 && ` – ${pos.rabatt}% Rabatt`}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-gray-900 shrink-0">
                        {formatWaehrung(total_pos)}
                      </p>
                    </div>
                  </div>
                )
              })}
              <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
                <p className="text-sm font-bold text-gray-700">Total</p>
                <p className="text-lg font-bold text-gray-900">{formatWaehrung(total)}</p>
              </div>
            </div>
          )}
        </Card>

        {/* Löschen */}
        <Button
          variant="danger" fullWidth size="sm" icon={<Trash2 size={14} />}
          onClick={() => setDeleteOpen(true)}
        >
          Offerte löschen
        </Button>
      </div>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Offerte bearbeiten"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button fullWidth loading={updateMutation.isPending}
              onClick={() => editForm && updateMutation.mutate({
                ...data,
                status: editForm.status as Offerte['status'],
                gueltig_bis: editForm.gueltig_bis || undefined,
                notizen: editForm.notizen || undefined,
              })}>
              Speichern
            </Button>
          </div>
        }
      >
        {editForm && (
          <div className="space-y-4">
            <Select label="Status" value={editForm.status}
              onChange={(e) => setEditForm(f => f ? { ...f, status: e.target.value } : f)}
              options={[
                { value: 'entwurf', label: 'Entwurf' },
                { value: 'gesendet', label: 'Gesendet' },
                { value: 'angenommen', label: 'Angenommen' },
                { value: 'abgelehnt', label: 'Abgelehnt' },
                { value: 'abgelaufen', label: 'Abgelaufen' },
              ]}
            />
            <Input label="Gültig bis" type="date" value={editForm.gueltig_bis}
              onChange={(e) => setEditForm(f => f ? { ...f, gueltig_bis: e.target.value } : f)} />
            <Textarea label="Notizen" value={editForm.notizen} rows={3}
              onChange={(e) => setEditForm(f => f ? { ...f, notizen: e.target.value } : f)} />
          </div>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Offerte löschen?"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
            <Button variant="danger" fullWidth loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}>
              Löschen
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700">
          Soll <strong>{data.nummer}</strong> wirklich gelöscht werden?
        </p>
      </Modal>
    </>
  )
}
